import { useLocale, type Locale } from '@/i18n/locale'

/**
 * Logo wall — подложка доверия под hero.
 *
 * У каждого бренда — свой типографический жест, имитирующий реальную
 * айдентику (паттерн a la Anthropic/Stripe press walls). Когда Тони
 * пришлёт настоящие SVG/PNG логотипы — заменяем JSX каждого бренда на
 * <img src="/logos/..." className="h-8 w-auto" /> и всё.
 *
 * Разрешения на публикацию от всех клиентов есть.
 *
 * i18n: scale-подписи и кириллические wordmark'и локализуются (наши
 * wordmark'и — типографическая имитация, не реальные лого, поэтому
 * латинизация на /en допустима и убирает кириллицу со страницы).
 */

type Client = {
  id: string
  scale: Record<Locale, string>
  /** JSX wordmark на локаль — каждому бренду свой стиль (шрифт/spacing/accent) */
  mark: Record<Locale, React.ReactNode>
}

const sameMark = (node: React.ReactNode): Record<Locale, React.ReactNode> => ({
  ru: node,
  en: node,
})

const CLIENTS: Client[] = [
  {
    id: 'novavi',
    scale: { ru: '$90K → $300K/мес', en: '$90K → $300K/mo' },
    mark: sameMark(
      <span className="font-serif italic text-[1.75rem] leading-none tracking-tight">
        NovaVi<span className="text-accent">.</span>
      </span>,
    ),
  },
  {
    id: 'roofmaster',
    scale: { ru: '×2 выручка, EU', en: '×2 revenue, EU' },
    mark: sameMark(
      <span className="font-sans font-bold uppercase text-[1.15rem] tracking-[0.18em] leading-none">
        Roofmaster
      </span>,
    ),
  },
  {
    id: 'drivemax',
    scale: { ru: '$25M → $40M, USA', en: '$25M → $40M, USA' },
    mark: sameMark(
      <span className="font-mono text-[1.3rem] font-semibold uppercase tracking-tight leading-none">
        Drive<span className="text-accent">/</span>max
      </span>,
    ),
  },
  {
    id: 'marposadkabel',
    scale: { ru: '400М → 2+ млрд ₽', en: '₽400M → ₽2B+' },
    mark: {
      ru: (
        <span className="font-sans font-semibold text-[1.1rem] tracking-[0.02em] leading-none">
          МАРПОСАД<span className="text-muted">КАБЕЛЬ</span>
        </span>
      ),
      en: (
        <span className="font-sans font-semibold text-[1.1rem] tracking-[0.02em] leading-none">
          MARPOSAD<span className="text-muted">KABEL</span>
        </span>
      ),
    },
  },
  {
    id: 'karate-sushi',
    scale: { ru: '×10 за 5 лет', en: '×10 in 5 years' },
    mark: {
      ru: (
        <span className="font-serif text-[1.35rem] leading-none tracking-tight">
          Карате<span className="text-accent"> · </span>Суши
        </span>
      ),
      en: (
        <span className="font-serif text-[1.35rem] leading-none tracking-tight">
          Karate<span className="text-accent"> · </span>Sushi
        </span>
      ),
    },
  },
  /**
   * Клиент под NDA. Имя и опознаваемые детали не публикуются — только
   * сектор и масштаб результата. Метка нарочно не мимикрирует под
   * логотип: пунктирный контур читается как «клиент есть, имени нет»,
   * а не как ещё один бренд в ряду.
   */
  {
    id: 'nda-fintech',
    scale: { ru: 'чистая прибыль ×2,25 за 3,5 мес', en: 'net profit ×2.25 in 3.5 months' },
    mark: {
      ru: (
        <span className="font-mono text-[0.75rem] uppercase tracking-[0.08em] leading-none text-muted border border-dashed border-divider rounded-btn px-3 py-1.5">
          NDA · Финтех
        </span>
      ),
      en: (
        <span className="font-mono text-[0.75rem] uppercase tracking-[0.08em] leading-none text-muted border border-dashed border-divider rounded-btn px-3 py-1.5">
          NDA · Fintech
        </span>
      ),
    },
  },
]

const COPY = {
  ru: {
    ariaLabel: 'Клиенты, с которыми работали до ИИ',
    heading: (
      <>
        65 проектов роста до ИИ —{' '}
        <span className="text-ink">методология, которую ИИ ускорил в разы</span>
      </>
    ),
  },
  en: {
    ariaLabel: 'Clients we worked with before AI',
    heading: (
      <>
        65 growth projects before AI —{' '}
        <span className="text-ink">a methodology AI has sped up many times over</span>
      </>
    ),
  },
}

export function ClientLogos() {
  const locale = useLocale()
  const t = COPY[locale]
  return (
    <section
      aria-label={t.ariaLabel}
      data-block="logos"
      className="py-10 lg:py-14"
    >
      <div className="container-x">
        <div className="text-small uppercase tracking-widest text-muted text-center">
          {t.heading}
        </div>
        <ul
          className="
            mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6
            gap-x-6 gap-y-10 items-start
          "
        >
          {CLIENTS.map((c) => (
            <li key={c.id} className="group text-center">
              <div
                className="
                  h-8 flex items-center justify-center
                  text-ink/80 group-hover:text-ink
                  transition-colors duration-300
                "
              >
                {c.mark[locale]}
              </div>
              {/* Цифры результата — моноширинным, как все данные на странице
                  (ТЗ 3A.1 п. 8б): текст абзацев — Inter, данные и метки — mono. */}
              <div
                className="
                  mt-3 font-mono text-[0.8125rem] leading-snug tabular-nums text-muted
                  transition-colors duration-300
                "
              >
                {c.scale[locale]}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
